import { Spinner, Text } from '@flatfile/baseline'
import { Suspense, memo, useCallback, useContext, useEffect } from 'react'
import { ErrorBoundary } from 'react-error-boundary'
import { useActiveEnvironment, useHasEnvironmentsAccess } from '../../hooks/useEnvironments'
import useResourceList from '../../resources/hooks/useResourceList'
import { ResourceListContext } from './ResourceListContext'
import { ListPagination, ResourceHeaderComponent, ResourceRowComponent } from './types'
import { ResourceType } from '../../resources/types'
import { ErrorBoundaryErrorPage, ErrorResponse, ErrorType } from '../Shared/CustomErrorComponent'

interface IResourceList {
  headerComponent: ResourceHeaderComponent
  rowComponent: ResourceRowComponent
  errorMessage: string
  emptyMessage: string
  pagination: ListPagination
  /**
   * Force mount is a way of unmounting and re-mounting a component
   * for circumstances where you want to clear the suspense cache -
   * by passing a new value to this prop that is different than the last
   * value passed, the component will be unmounted (busting cache) and re-mounted
   */
  forceMount?: number | string
  height?: number
  onRowActionSuccess?: (action: string) => void
}

interface IResourceListBody extends Omit<IResourceList, 'title' | 'errorMessage'> {
  pagination: ListPagination
}

export const EmptyTable = ({
  headerComponent: HeaderComponent,
  message,
  loading,
}: {
  headerComponent: ResourceHeaderComponent
  message?: string
  loading?: boolean
}) => {
  return (
    <>
      <table className='w-full border-collapse'>
        <HeaderComponent />
        <tbody />
      </table>
      <div className='flex items-center justify-center p-4'>
        {message && (
          <Text variant='body' color='muted'>
            {message}
          </Text>
        )}
        {loading && <Spinner className='mt-4' />}
      </div>
    </>
  )
}

export const ErrorTable = ({
  headerComponent: HeaderComponent,
  error,
  resourceType,
}: {
  headerComponent: ResourceHeaderComponent
  message?: string
  loading?: boolean
  error: ErrorResponse | ErrorType
  resourceType: ResourceType
}) => {
  return (
    <>
      <table className='w-full border-collapse'>
        <HeaderComponent />
        <tbody />
      </table>
      <div className='flex items-center justify-center'>
        <ErrorBoundaryErrorPage error={error} page={`ResourceTable - ${resourceType}`} doNotCenter />
      </div>
    </>
  )
}

export const ResourceTable = ({
  rowComponent: RowComponent,
  headerComponent: HeaderComponent,
  pagination,
  emptyMessage,
  onRowActionSuccess,
}: IResourceListBody) => {
  const { resourceType, requestParams, bulkSelectionIds, onSelectRowById, onSelectAll, onClearSelections } =
    useContext(ResourceListContext)
  const { environment, setActiveEnvironment } = useActiveEnvironment()
  const hasAnyEnvAccess = useHasEnvironmentsAccess()
  const { currentPage, setPageCount } = pagination
  const { ids, response } = useResourceList(resourceType, {
    environmentId: environment?.id,
    pageSize: 15,
    pageNumber: pagination.currentPage,
    filterByEnvId: hasAnyEnvAccess,
    ...requestParams,
  })

  useEffect(() => {
    setPageCount(response?.pagination?.pageCount || 0)
    onClearSelections()
  }, [currentPage])

  useEffect(() => {
    setPageCount(response?.pagination?.pageCount || 0)
    onClearSelections()
  }, [response?.pagination])

  useEffect(() => {
    if (!environment && hasAnyEnvAccess && response.data.length && response.data[0].environmentId)
      setActiveEnvironment(response.data[0].environmentId)
  }, [environment, hasAnyEnvAccess])

  const handleSelectAll = useCallback(() => {
    onSelectAll(ids)
  }, [onSelectAll, ids])

  return (
    <div className='w-full'>
      {!ids.length ? (
        <EmptyTable headerComponent={HeaderComponent} message={emptyMessage} />
      ) : (
        <table className='w-full border-collapse'>
          <HeaderComponent toggleAll={handleSelectAll} selections={bulkSelectionIds} ids={ids} />
          <tbody>
            {ids.map((id: string) => (
              <RowComponent
                key={id}
                resourceId={id}
                selected={bulkSelectionIds.includes(id)}
                onSelect={onSelectRowById}
                onRowActionSuccess={onRowActionSuccess}
              />
            ))}
          </tbody>
        </table>
      )}
    </div>
  )
}

export const ResourceList: React.FC<IResourceList> = memo(
  ({
    rowComponent: RowComponent,
    headerComponent: HeaderComponent,
    onRowActionSuccess,
    errorMessage,
    emptyMessage,
    pagination,
    forceMount,
  }: IResourceList) => {
    const { resourceType } = useContext(ResourceListContext)
    return (
      <div className='w-full'>
        <ErrorBoundary
          fallbackRender={({ error }) => (
            <ErrorTable
              message={errorMessage}
              headerComponent={HeaderComponent}
              error={error}
              resourceType={resourceType}
            />
          )}
        >
          <Suspense fallback={<EmptyTable loading headerComponent={HeaderComponent} />}>
            <div className='w-full h-full'>
              <ResourceTable
                key={forceMount}
                pagination={pagination}
                headerComponent={HeaderComponent}
                rowComponent={RowComponent}
                emptyMessage={emptyMessage}
                onRowActionSuccess={onRowActionSuccess}
              />
            </div>
          </Suspense>
        </ErrorBoundary>
      </div>
    )
  }
)

ResourceList.displayName = 'ResourceList'

export default ResourceList
